// Plan 23a Task 4.2 — Settings Toggle primitive (sliding thumb on/off switch).
// Behavior contract: docs/design/components.md §21 lines 665–675.

import type { UIComponentHandle, UIComponentConfig } from './types';
import type { GsapTimelineHandle } from '../../renderer/vfx/gsap-registry';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export interface ToggleOpts {
  /** Label rendered to the left of the track. */
  label: string;
  /** Initial value. Default: false. */
  value?: boolean;
  /** Called only on user-initiated clicks, never from setValue(). */
  onChange?: (v: boolean) => void;
}

export interface SettingsToggleHandle extends UIComponentHandle {
  /** Current on/off state. */
  readonly value: boolean;
  /** Programmatically set the state. Does NOT fire onChange. */
  setValue(v: boolean): void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Animation + colour constants — all values from docs/design/tokens.json
// ─────────────────────────────────────────────────────────────────────────────

/** animation.duration.fast = 150ms expressed in seconds for GSAP */
const SLIDE_DURATION_S = 0.15;

/** animation.easing.snappy from tokens.json */
const SNAPPY_EASE = 'cubic-bezier(0.2, 0, 0, 1)';

/** color.ui.panel-bg — track "off" background */
const PANEL_BG = '#1E2332';

/** color.brand.amber — track "on" background */
const AMBER = '#E8A020';

/** Thumb x-offset when ON (px): track 40px wide, thumb 18px → 40-18-2(left inset)=20 → 18 per spec */
const THUMB_X_ON = 18;

/** Thumb x-offset when OFF (px) */
const THUMB_X_OFF = 0;

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createSettingsToggle(config: UIComponentConfig & ToggleOpts): SettingsToggleHandle {
  const { gsapRegistry, mount, label, value: initialValue = false, onChange } = config;

  let _value = initialValue;
  let thumbX = _value ? THUMB_X_ON : THUMB_X_OFF;
  let disposed = false;
  let currentHandle: GsapTimelineHandle | null = null;

  // ── Build DOM ──────────────────────────────────────────────────────────────

  // Outer container: flex row, items aligned vertically
  const el = document.createElement('div');
  el.className = 'ui-settings-toggle';
  el.setAttribute('role', 'switch');
  el.setAttribute('aria-checked', String(_value));
  el.setAttribute('tabindex', '0');
  el.style.cssText = [
    'display: flex',
    'flex-direction: row',
    'align-items: center',
    'min-height: 48px',
    // spacing.sm between label and track
    'gap: var(--spacing-sm, 8px)',
    'cursor: pointer',
    'touch-action: manipulation',
  ].join('; ');

  // Label — typography.family.body, typography.size.body, color.text.secondary
  const labelEl = document.createElement('span');
  labelEl.className = 'toggle-label';
  labelEl.textContent = label;
  labelEl.style.cssText = [
    // typography.family.body
    "font-family: var(--typography-family-body, 'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif)",
    // typography.size.body = 16px
    'font-size: var(--typography-size-body, 16px)',
    // color.text.secondary = #B0B8C8
    'color: var(--color-text-secondary, #B0B8C8)',
    'user-select: none',
  ].join('; ');

  // Track: mobile-safe switch target, border-radius.pill
  const track = document.createElement('div');
  track.className = 'toggle-track';
  track.style.cssText = [
    'width: 44px',
    'height: 26px',
    // border-radius.pill = 9999px
    'border-radius: var(--border-radius-pill, 9999px)',
    // Initial background based on initial value
    `background-color: ${_value ? AMBER : PANEL_BG}`,
    'position: relative',
    'cursor: pointer',
    'flex-shrink: 0',
  ].join('; ');

  // Thumb: 18px white circle, shadow.card
  const thumb = document.createElement('div');
  thumb.className = 'toggle-thumb';
  thumb.style.cssText = [
    'width: 18px',
    'height: 18px',
    // border-radius.circle = 50%
    'border-radius: var(--border-radius-circle, 50%)',
    // color.text.primary = white
    'background: #FFFFFF',
    'background-color: #FFFFFF',
    // shadow.card = 0 2px 8px rgba(0,0,0,0.30)
    'box-shadow: var(--shadow-card, 0 2px 8px rgba(0,0,0,0.30))',
    'position: absolute',
    'top: 2px',
    'left: 2px',
    `transform: translateX(${thumbX}px)`,
    'display: block',
    'box-sizing: border-box',
    'will-change: transform',
  ].join('; ');

  track.appendChild(thumb);
  el.appendChild(labelEl);
  el.appendChild(track);

  // ── Helpers ────────────────────────────────────────────────────────────────

  function killCurrent(): void {
    if (currentHandle) {
      currentHandle.dispose();
      currentHandle = null;
    }
  }

  // ── setValue ───────────────────────────────────────────────────────────────

  function setValue(v: boolean): void {
    if (disposed) return;
    _value = v;
    el.setAttribute('aria-checked', String(_value));

    // Kill any in-flight slide animation
    killCurrent();

    // Create a new parallel timeline for thumb slide + track color.
    // Both tweens start at position 0 (parallel, NOT chained-append).
    // This is the F20a-CLASS-1 sentinel-safe pattern.
    const handle = gsapRegistry.create('toggle:slide');
    const tl = handle.timeline;

    // Thumb slides: x = 0 (off) or THUMB_X_ON (on)
    const thumbTween = { x: thumbX };
    tl.to(
      thumbTween,
      {
        x: v ? THUMB_X_ON : THUMB_X_OFF,
        duration: SLIDE_DURATION_S,
        ease: SNAPPY_EASE,
        onUpdate: () => {
          thumbX = thumbTween.x;
          thumb.style.transform = `translateX(${thumbX}px)`;
        },
        onComplete: () => {
          thumbX = v ? THUMB_X_ON : THUMB_X_OFF;
          thumb.style.transform = `translateX(${thumbX}px)`;
        },
      },
      0, // explicit position — F20a-CLASS-1
    );

    // Track color transitions simultaneously
    tl.to(
      track,
      {
        backgroundColor: v ? AMBER : PANEL_BG,
        duration: SLIDE_DURATION_S,
        ease: SNAPPY_EASE,
      },
      0, // explicit position — F20a-CLASS-1
    );

    currentHandle = handle;
  }

  // ── Click handler ──────────────────────────────────────────────────────────

  function handleClick(event?: Event): void {
    if (disposed) return;
    event?.preventDefault();
    // Toggle the value
    const next = !_value;
    setValue(next);
    // Only click fires onChange
    onChange?.(next);
  }

  el.addEventListener('click', handleClick);
  el.addEventListener('keydown', handleKeydown);

  function handleKeydown(event: KeyboardEvent): void {
    if (event.target !== el) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    handleClick(event);
  }

  // ── dispose ────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Kill any in-flight animation
    killCurrent();

    // Remove click listener
    el.removeEventListener('click', handleClick);
    el.removeEventListener('keydown', handleKeydown);

    // Detach from DOM if mounted
    if (el.parentNode) {
      el.parentNode.removeChild(el);
    }
  }

  // ── Optional mount at construction time ───────────────────────────────────

  if (mount && !mount.contains(el)) {
    mount.appendChild(el);
  }

  thumb.style.transform = `translateX(${thumbX}px)`;
  track.style.backgroundColor = _value ? AMBER : PANEL_BG;

  return {
    element: el,
    get value(): boolean {
      return _value;
    },
    setValue,
    dispose,
  };
}
